/**
 * What a registration sends, and what actually stops one.
 *
 * A bucket is the only entity in this product. Nothing groups one to another,
 * and `RegisterBucketRequest.companyId` is optional in the contract for
 * exactly that reason: omit it and the server mints an owner for this bucket
 * alone. Both call sites went on sending `companies.items[0].id`, which is how
 * `aiinsurance26`, `capway` and `ybot`, three unrelated corpora, ended up
 * under one company record, and how a "whole-company" analysis started
 * reaching across all three.
 *
 * The old comment above each call site said the companyId was "the backend's
 * constraint, not a choice offered here". That was true when it was written
 * and stopped being true when ask 4 shipped. A constraint quoted from memory
 * outlives the constraint.
 *
 * The refusals live here rather than inside either component because there
 * were two copies of them and they had already drifted: the S3 page checked
 * the region and the account and said which was missing, while the search
 * checked both at once and named neither, and had a third branch, a missing
 * company grant, that returned without saying anything at all.
 */
import type { RegisterBucketRequest } from '@/api/backend';

/** The row a registration is built from: what the account listing reported. */
export interface ObservedScope {
  name: string;
  /** S3 does not report a region when listing an account, so this is often null. */
  region: string | null;
  /** The AWS account the listing was observed in, or null when genuinely unknown. */
  accountId: string | null;
  /**
   * The folder inside the bucket, or `''` for the whole bucket.
   *
   * A scope has always been `(account, bucket, prefix)` rather than a bucket:
   * `buckets.scope_prefix` is part of the unique key, discovery lists inside
   * it, and the walk carries it. The picker simply never asked, so every
   * registration was a whole bucket. `s3://capway/` and
   * `s3://capway/imports/github-repo/` are two separate scopes with their own
   * runs, packages and handovers, and neither can see the other.
   */
  prefix?: string;
}

/**
 * Why this row cannot be registered yet, in the operator's own terms, or null.
 *
 * Both facts are written into the record and neither can be guessed. A region
 * decides which endpoint reads the bucket; an account id names whose bucket it
 * is. Registering without either means writing a value nobody observed into a
 * record the rest of the estate then treats as evidence.
 */
export function whyNotRegistrable(scope: ObservedScope): string | null {
  if (!scope.name.trim()) {
    return 'Name the bucket to register it.';
  }
  const prefixProblem = whyNotAPrefix(scope.prefix);
  if (prefixProblem) return `${scope.name}: ${prefixProblem}`;
  if (!scope.region || !scope.region.trim()) {
    return `${scope.name}: neither the account listing nor an existing registration names a region, type one in the row, then register.`;
  }
  if (!scope.accountId) {
    return `${scope.name}: the account listing did not report which AWS account these buckets were observed in, and no registration exists to read it from. Registering would have to guess it, and the guess is written into the record.`;
  }
  return null;
}

/**
 * The body of a registration.
 *
 * No `companyId`. The key is absent rather than undefined, because the
 * difference is visible on the wire and "the client omitted it" is the exact
 * signal the server reads to mint an owner.
 */
export function registerBody(scope: ObservedScope): RegisterBucketRequest {
  const refusal = whyNotRegistrable(scope);
  if (refusal || !scope.accountId || !scope.region) throw new Error(refusal ?? `${scope.name}: not registrable.`);
  return {
    accountId: scope.accountId,
    name: scope.name.trim(),
    region: scope.region.trim(),
    // `''` is the whole-bucket scope and is a real value, not a null. Omitting
    // it or sending null does not mean "the bucket"; it fails the contract.
    prefix: normalisePrefix(scope.prefix),
    role: 'SOURCE_EVIDENCE',
  };
}

/**
 * Why this is not a folder prefix, or null.
 *
 * The trailing slash is enforced by a CHECK in the database, so
 * `imports/github-repo` is refused and `imports/github-repo/` is accepted.
 * Saying so here means the operator learns it from the field rather than from
 * a 400 after pressing register.
 */
export function whyNotAPrefix(prefix: string | undefined): string | null {
  const value = (prefix ?? '').trim();
  if (value === '') return null;
  if (value.startsWith('/')) return 'a folder is written without a leading slash, as `imports/github-repo/`.';
  if (value.includes('//')) return 'a folder has no empty segment; remove the doubled slash.';
  // eslint-disable-next-line no-control-regex
  if (/\u0000/.test(value)) return 'a folder cannot contain a null byte.';
  return null;
}

/**
 * The prefix as the API wants it: `''`, or ending in exactly one `/`.
 *
 * A trailing slash is added rather than refused. An operator who types
 * `imports/github-repo` means the folder, and rejecting that to teach them
 * about a database CHECK is the product being pedantic about its own schema.
 */
export function normalisePrefix(prefix: string | undefined): string {
  const value = (prefix ?? '').trim();
  if (value === '') return '';
  return value.endsWith('/') ? value : `${value}/`;
}

/**
 * What to call a scope on screen.
 *
 * Two scopes over one bucket are genuinely separate - their own runs, their
 * own packages, their own handovers - and they arrive carrying the same
 * bucket name. Drawing both as `capway` is how someone concludes they are
 * looking at everything when they are looking at one folder.
 */
export function scopeLabel(name: string, prefix: string | null | undefined): string {
  const value = (prefix ?? '').trim();
  return value === '' ? name : `${name}/${value.replace(/\/$/, '')}`;
}

/**
 * Scopes already registered that this new one would overlap.
 *
 * Registering `capway/` and then `capway/imports/` means every object under
 * `imports/` belongs to both, and both would deliver it. The backend allows
 * this deliberately - it is the tenant's business - so the only thing owed is
 * that nobody creates one without being told.
 */
export function overlaps(
  scope: { name: string; prefix?: string },
  existing: readonly { name: string; prefix: string }[],
): { name: string; prefix: string }[] {
  const prefix = normalisePrefix(scope.prefix);
  return existing.filter((other) => {
    if (other.name !== scope.name) return false;
    const theirs = normalisePrefix(other.prefix);
    if (theirs === prefix) return false; // the same scope, refused by the unique key
    return theirs.startsWith(prefix) || prefix.startsWith(theirs);
  });
}

/**
 * Whether the AWS account listing can be read at all, and what to say when not.
 *
 * `GET /api/v1/account-buckets` still takes a required `companyId`, so reading
 * the account's observed buckets needs an existing grant as a key even though
 * registering one no longer does. That asymmetry is a backend ceiling, it is
 * ask 8 in `docs/BACKEND_ASKS.md`, and the page states it rather than
 * standing in for it: the whole S3 page used to be replaced by "No company
 * context", which reads as though registration were barred, and described a
 * rule the contract had already dropped.
 */
export function accountListingRefusal(grant: string | null | undefined): string | null {
  if (grant) return null;
  return 'The AWS account listing is read with an existing grant as its key, and this identity holds none yet. Registering a bucket no longer needs one, this listing does.';
}
